import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { useState, type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled, { keyframes } from 'styled-components';

import {
  ManagerProgressionTaskState,
  TaskLineupType,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh';
import { Target } from '@sorare/core/src/atoms/icons/Target';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import {
  ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
  ANY_SPORT_SERIES_TASK_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { absCenter } from '@sorare/core/src/lib/style';

import { ReplayIntroDialog } from '#src/components/series/ReplayIntroDialog/index.tsx';
import { ArrowDownShape as ArrowDownShapeSvg } from '#src/components/series/SeriesBoard/ArrowDownShape/index.tsx';
import { Lifes } from '#src/components/series/SeriesBoard/Lifes/index.tsx';
import { SeriesBoardOnboarding } from '#src/components/series/SeriesBoard/SeriesBoardOnboarding/index.tsx';
import {
  DIAMOND_HEIGHT,
  DIAMOND_WIDTH,
} from '#src/components/series/SeriesBoard/utils.ts';

import { ClaimableTile } from './ClaimableTile/index.tsx';
import { FailedTile } from './FailedTile/index.tsx';
import { ReadyAndSelectedTile } from './ReadyAndSelectedTile/index.tsx';
import { ReadyTile } from './ReadyTile/index.tsx';
import type { HotStreakTile_thresholdPickerTask } from './__generated__/index.graphql.ts';
import checkmarkTile from './assets/checkmarkTile.webp';
import fireIcon from './assets/fireIcon.svg';
import lockedTile from './assets/lockedTile.webp';
import replayIcon from './assets/replayIcon.webp';

const fireIconAnimation = keyframes`
  0% {
    transform: translateY(-10px) translateX(-50%);
  }
  40% {
    transform: translateY(0px) translateX(-50%);
  }
  100% {
    transform: translateY(-10px) translateX(-50%);
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${DIAMOND_WIDTH}px;
  height: ${DIAMOND_HEIGHT}px;
`;
const CheckmarkTile = styled.div`
  ${absCenter}
  width: 100%;
  height: 100%;
  background: url(${checkmarkTile}) no-repeat center / contain;
`;
const ReadyIcon = styled.div`
  width: ${DIAMOND_WIDTH * 1.6}px;
  height: ${DIAMOND_HEIGHT * 1.6}px;
  background: url(${fireIcon}) no-repeat center / contain;

  &.replay {
    background: url(${replayIcon}) no-repeat center / contain;
  }
`;
const ReadyIconWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 20px;
  animation: ${fireIconAnimation} 4s ease-in-out infinite;
`;
const CroppedPlayerImage = styled.div`
  position: absolute;
  top: -51%;
  width: 100px;
  height: 100px;
  clip-path: polygon(15% 0%, 15% 81.17%, 50% 99.89%, 84% 81.17%, 84% 0%);
`;
const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  z-index: 1;
`;
const LiveChip = styled(Chip)`
  position: absolute;
  top: calc(-1 * var(--quadruple-unit));
  left: 80%;
  color: var(--c-red-400);
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--c-red-400);
`;

const greenOverlay = 'linear-gradient(to top, #00F34D 10%, transparent 35%)';

const redOverlay = 'linear-gradient(to top, #721717 10%, transparent 35%)';

const Separator = styled.span`
  width: 1px;
  height: var(--intermediate-unit);
  background: var(--c-nd-150);
  margin: 0 4px;
`;

const Strikethrough = styled.span`
  text-decoration: line-through;
  color: var(--c-nd-400);
`;

type Props = {
  task: HotStreakTile_thresholdPickerTask;
  taskIndex: number;
  style?: CSSProperties;
  showOnboarding?: boolean;
  onClaimTask: (id: string) => void;
  onOpenHotStreaksTutorialStory?: () => void;
  showTargetValue?: boolean;
  setShowTargetValue?: (showTargetValue: boolean) => void;
};
export const HotStreakTile = ({
  task,
  taskIndex,
  style,
  showOnboarding = false,
  onClaimTask,
  onOpenHotStreaksTutorialStory,
  showTargetValue = false,
  setShowTargetValue,
}: Props) => {
  const { generateSportContextPath, sport } = useSportContext();
  const navigate = useNavigate();
  const { lifecycle } = useLifecycle();
  const [openReplayIntroDialog, setOpenReplayIntroDialog] = useState(false);
  const {
    flags: { setThresholdReduction = {} },
  } = useFeatureFlags();
  const { id, aasmState, rewardConfigs, replay, displayedPlayer, taskLineups } =
    task;
  const taskId = idFromObject(id);

  const lineupsPath = generateSportContextPath(ANY_SPORT_SERIES_TASK_LINEUPS, {
    params: { task: taskId },
  });

  const composePath = generateSportContextPath(
    ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
    {
      params: { task: taskId },
    }
  );

  const valueFromFeatureFlag =
    setThresholdReduction[sport.toLowerCase()]?.[taskIndex];
  const oldThreshold =
    typeof valueFromFeatureFlag === 'number' ? valueFromFeatureFlag : null;
  const hasReducedThreshold =
    !replay && !!oldThreshold && oldThreshold > task.target;

  const tooltipContent = (
    <Horizontal gap={0.5}>
      <RewardConfigImage anyRewardConfig={rewardConfigs[0]} size={3} />
      {!showTargetValue && (
        <BrandXXS>
          <RewardConfigText anyRewardConfig={rewardConfigs[0]} />
        </BrandXXS>
      )}
      <Separator />
      <Target />{' '}
      {!!showTargetValue && (
        <BrandXXS>
          {task.target}&nbsp;
          {!!hasReducedThreshold && (
            <Strikethrough>{oldThreshold}</Strikethrough>
          )}
        </BrandXXS>
      )}
    </Horizontal>
  );

  const hasSubmittedLineups = taskLineups.length > 0;
  const hasFailedAllSubmittedLineups =
    hasSubmittedLineups &&
    taskLineups.every(lineup => lineup.aasmState === TaskLineupType.FAILED);

  const hasFailedReplay =
    replay &&
    aasmState === ManagerProgressionTaskState.READY &&
    hasFailedAllSubmittedLineups;

  if (
    hasFailedReplay ||
    task.aasmState === ManagerProgressionTaskState.CANCELED
  ) {
    return (
      <Wrapper style={style}>
        <StyledButtonBase to={replay ? composePath : lineupsPath}>
          <FailedTile />
          <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
            <Vertical center>
              <Refresh />
              <BrandS>
                <FormattedMessage defaultMessage="Miss" />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </StyledButtonBase>
      </Wrapper>
    );
  }

  if (aasmState === ManagerProgressionTaskState.READY) {
    if (hasSubmittedLineups) {
      if (hasFailedAllSubmittedLineups) {
        return (
          <Wrapper style={style}>
            <StyledButtonBase to={lineupsPath}>
              <FailedTile />
              <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${redOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
                <LiveChip color="black">
                  <Lifes task={task} size={1} />
                </LiveChip>
              </ArrowDownShapeSvg>
            </StyledButtonBase>
          </Wrapper>
        );
      }

      const hasLiveGames = task.taskLineups.some(lineup => lineup.hasLiveGames);
      return (
        <Wrapper style={style}>
          <StyledButtonBase to={lineupsPath}>
            <ReadyAndSelectedTile />
            <ArrowDownShapeSvg
              animatedOnHover
              colors={{ start: '#00F34D', end: '#00D5FF' }}
            >
              <CroppedPlayerImage
                style={{
                  background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                }}
              />
              {!!hasLiveGames && (
                <LiveChip color="black">
                  <Dot />
                  <BrandXXS>
                    <FormattedMessage defaultMessage="Live" />
                  </BrandXXS>
                </LiveChip>
              )}
            </ArrowDownShapeSvg>
          </StyledButtonBase>
        </Wrapper>
      );
    }
    const to = replay ? composePath : lineupsPath;

    const onClick = () => {
      if (replay) {
        setOpenReplayIntroDialog(true);
        return;
      }
      if (!lifecycle?.sawSeriesHotStreakOnboarding) {
        onOpenHotStreaksTutorialStory?.();
        return;
      }
      navigate(to);
    };

    return (
      <>
        <OnboardingHighlightWrapper
          highlightName="SeriesBoardReplayButton"
          wrap={!!showOnboarding}
        >
          <Wrapper style={style}>
            <ButtonBase
              onClick={() => {
                if (replay) {
                  setOpenReplayIntroDialog(true);
                  return;
                }
                if (!lifecycle?.sawSeriesHotStreakOnboarding) {
                  onOpenHotStreaksTutorialStory?.();
                  return;
                }
                navigate(to);
              }}
            >
              <ReadyTile />
              <ReadyIconWrapper>
                <Tooltip
                  open
                  placement="top"
                  onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
                  showInPortal={false}
                  title={tooltipContent}
                >
                  <ReadyIcon className={classNames({ replay })} />
                </Tooltip>
              </ReadyIconWrapper>
            </ButtonBase>
          </Wrapper>
        </OnboardingHighlightWrapper>
        {!!showOnboarding && <SeriesBoardOnboarding onClose={onClick} />}
        <ReplayIntroDialog
          task={task}
          open={openReplayIntroDialog}
          onClose={() => setOpenReplayIntroDialog(false)}
          to={to}
        />
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    return (
      <Wrapper style={style}>
        <StyledButtonBase
          {...(replay
            ? { onClick: () => onClaimTask(id) }
            : { to: lineupsPath })}
        >
          <ClaimableTile />
          <ArrowDownShapeSvg floating>
            <Vertical gap={0} center>
              <RewardConfigImage anyRewardConfig={rewardConfigs[0]} />
              <BrandS>
                <RewardConfigText anyRewardConfig={rewardConfigs[0]} />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </StyledButtonBase>
      </Wrapper>
    );
  }

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return (
      <Wrapper style={style}>
        <StyledButtonBase
          to={replay ? undefined : lineupsPath}
          as={replay ? 'div' : undefined}
        >
          <CheckmarkTile />
          <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
            <CroppedPlayerImage
              style={{
                background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
              }}
            />
          </ArrowDownShapeSvg>
        </StyledButtonBase>
      </Wrapper>
    );
  }

  if (aasmState === ManagerProgressionTaskState.ASSIGNED) {
    return (
      <Wrapper style={style}>
        <Tooltip
          open
          placement="top"
          showInPortal={false}
          onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
          title={tooltipContent}
        >
          <img src={lockedTile} alt="locked tile" width={30} />
        </Tooltip>
      </Wrapper>
    );
  }

  return null;
};

HotStreakTile.fragments = {
  thresholdPickerTask: gql`
    fragment HotStreakTile_thresholdPickerTask on ThresholdPickerTask {
      id
      aasmState
      replay
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
      }
      displayedPlayer {
        slug
        squaredPictureUrl
      }
      taskLineups {
        id
        aasmState
        hasLiveGames
      }
      ...ReplayIntroDialog_taskInterface
      ...Lifes_thresholdPickerTask
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${ReplayIntroDialog.fragments.taskInterface}
    ${Lifes.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<HotStreakTile_thresholdPickerTask>,
};
